import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Shell, ROLE_LABEL } from "@/app/Shell";
import { useMe } from "@/app/session";
import { RequestError } from "@/app/queries";
import { isActive, useRun, useRuns, type Run } from "@/app/gradingQueries";
import { useAssignment, useWorkspace, type AssignmentView, runnerAvailable } from "@/app/notebookQueries";
import type { Notebook, Task } from "@/kernel/runner";
import { count } from "@/lib/plural";
import { Button } from "@/ui/Button";
import { EmptyState, NoAccess, NotFound, SkeletonRows, StateCard } from "@/ui/states";
import { AfterCard, RunsCard, SubmitCard } from "./check/Aside";
import { Failures } from "./check/Failures";
import { GridSkeleton, TaskGrid } from "./check/TaskGrid";
import { useServerCheck, useSubmitFlow } from "./check/hooks";
import { ENV_KIND, failures, isStuck, runMeta, summary } from "./check/model";

type User = { name: string; role: string } | undefined;

/** S6 «Результат автопроверки и сдача»: прогон на сервере, упавшие проверки,
 *  условия отправки ментору. Прогон по умолчанию — последний. */
export function CheckPage({ assignmentId, runId }: { assignmentId: string; runId?: string }) {
  const me = useMe();
  const user: User = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const asg = useAssignment(assignmentId);
  const runs = useRuns(assignmentId);
  const selectedId = runId ?? runs.data?.[0]?.id;
  const run = useRun(selectedId);
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );

  const retry = () => (void asg.refetch(), void runs.refetch(), void run.refetch());
  const err = asg.error ?? runs.error ?? (runId ? run.error : undefined);
  if (err instanceof RequestError && err.code === "step_locked")
    return frame(<StateCard code="LOCKED" where="автопроверка" title="Шаг ещё закрыт" body={err.message} primary={{ label: "вернуться к маршруту", href: "/" }} />);
  if (err instanceof RequestError && err.status === 403)
    return frame(<NoAccess where="автопроверка" body="Результаты автопроверки видит студент, который сдаёт домашку, и его ментор." primary={{ label: "на мой маршрут", href: "/" }} />);
  if (err instanceof RequestError && err.status === 404) return frame(<NotFound />);
  if (err)
    return frame(
      <StateCard
        code="ERR_NETWORK"
        where="автопроверка"
        tone="warn"
        title="Нет связи с сервером"
        body="Результаты прогона не загрузились. Прогоны и черновик хранятся на сервере — ничего не потеряно."
        primary={{ label: "повторить сейчас", onClick: retry }}
      />,
    );
  if (!asg.data || !runs.data) return frame(<SkeletonRows />);
  if (!runnerAvailable(asg.data.manifest.runtime))
    return frame(
      <StateCard
        code="NO_RUNNER"
        where="автопроверка"
        title="У этой домашки нет автопроверки"
        body="Среда задания в браузере не выполняется, а серверные раннеры ещё не подключены. Реши ноутбук локально или в Colab и отправь ментору — он проверит код вручную."
        primary={{ label: "к ноутбуку", href: `/notebook/${assignmentId}` }}
      />,
    );
  if (runs.data.length === 0) return <NoRuns asg={asg.data} user={user} />;
  const current = run.data ?? runs.data.find((r) => r.id === selectedId);
  if (!current) return frame(<SkeletonRows />);
  return <Check asg={asg.data} run={current} runs={runs.data} user={user} />;
}

function Breadcrumb({ asg, meta }: { asg: AssignmentView; meta?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-bd px-5 py-2.5">
      <span className="font-mono text-[12px] text-mut" data-testid="breadcrumb">
        <Link to="/homework" className="text-mut no-underline hover:text-ink">
          домашки
        </Link>{" "}
        /{" "}
        <Link to="/notebook/$assignmentId" params={{ assignmentId: asg.id }} className="text-mut no-underline hover:text-ink">
          {asg.file_name}
        </Link>{" "}
        / прогон
      </span>
      <span className="flex-1" />
      {meta && (
        <span className="font-mono text-[11.5px] text-mut" data-testid="run-meta">
          {meta}
        </span>
      )}
    </div>
  );
}

function Title({ total }: { total: number }) {
  return (
    <div className="mb-[18px] flex flex-wrap items-baseline gap-3.5">
      <h1 className="m-0 font-mono text-[22px] font-bold tracking-[-.015em]">Автопроверка · {count(total, "задача", "задачи", "задач")}</h1>
      <span className="text-[13px] text-mut">раннер выполняет ячейки с assert до того, как ноутбук увидит ментор</span>
    </div>
  );
}

function NoRuns({ asg, user }: { asg: AssignmentView; user: User }) {
  const check = useServerCheck(asg.id);
  const total = asg.manifest.tasks.length;
  return (
    <Shell wide user={user}>
      <Breadcrumb asg={asg} />
      <div className="mx-auto flex max-w-[760px] flex-col gap-3 px-6 py-8">
        <Title total={total} />
        <EmptyState
          where="автопроверка · прогоны"
          title="Прогонов ещё нет"
          marks={Array.from({ length: total }, () => false)}
          body="Сервер выполнит ноутбук целиком на CPU и прогонит проверки всех задач — так же, как «запустить всё» в браузере. Черновик берётся последний сохранённый; результат появится здесь, а ментор увидит только тот прогон, который ты отправишь."
          primary={{ label: check.busy ? "ставим в очередь…" : "проверить на сервере", onClick: () => void check.run() }}
          alt={{ label: "вернуться в ноутбук", href: `/notebook/${asg.id}` }}
        />
        {check.error && (
          <p role="alert" className="m-0 text-[12.5px]" style={{ color: "var(--err)" }}>
            {check.error}
          </p>
        )}
      </div>
    </Shell>
  );
}

function Stat({ id, n, label, fg }: { id: string; n: ReactNode; label: string; fg: string }) {
  return (
    <div className="rounded-md border border-bd bg-card px-4 py-3.5" style={{ borderTop: `2px solid ${fg}` }}>
      <div className="font-mono text-[28px] font-bold leading-none" style={{ color: fg }} data-testid={`stat-${id}`}>
        {n}
      </div>
      <div className="mt-2 font-mono text-[11.5px] text-mut">{label}</div>
    </div>
  );
}

function Pending({ run }: { run: Run }) {
  const stuck = isStuck(run);
  return (
    <div data-testid="run-pending" role="status" className="mb-5 rounded-md border border-bd bg-card px-4 py-3 text-[13px] leading-[1.55] text-ink2" style={{ borderLeft: `2px solid ${stuck ? "var(--warn)" : "var(--acc)"}` }}>
      {stuck ? (
        <>
          <strong className="text-ink">Раннер не отвечает.</strong> Прогон #{run.n} стоит в очереди больше минуты. Черновик сохранён; страница обновится сама, как только раннер возьмёт прогон.
        </>
      ) : (
        <>
          <span className="font-mono text-[12.5px] text-ink">раннер выполняет ячейки…</span> Прогон #{run.n} {run.status === "queued" ? "в очереди" : "идёт"}; результат появится здесь сам.
        </>
      )}
    </div>
  );
}

function EnvError({ run, onRetry, busy, error }: { run: Run; onRetry: () => void; busy: boolean; error: string | null }) {
  const env = run.result?.env_error;
  return (
    <section data-testid="env-error" role="alert" className="mb-5 overflow-hidden rounded-md border border-err bg-card">
      <div className="flex gap-2.5 border-b border-bd bg-errsoft px-3.5 py-[9px]">
        <span className="font-mono text-[10.5px] uppercase tracking-[.06em]" style={{ color: "var(--err)" }}>
          ошибка среды
        </span>
        <span className="flex-1" />
        <span className="font-mono text-[10.5px] text-ink2">{env ? (ENV_KIND[env.kind] ?? env.kind) : "прогон не завершился"}</span>
      </div>
      <div className="flex flex-col gap-2.5 px-[18px] py-4">
        <h2 className="m-0 font-mono text-[15px] font-bold text-ink">Прогон #{run.n} не дошёл до проверок: {env ? (ENV_KIND[env.kind] ?? env.kind) : "ошибка раннера"}</h2>
        <p className="m-0 text-[13px] leading-[1.55] text-ink2">Это не оценка твоего решения: раннер не смог выполнить ноутбук целиком. Черновик и прошлые прогоны на месте — ничего не потеряно.</p>
        {env?.message && <pre className="m-0 whitespace-pre-wrap rounded-sm border border-bd bg-bg2 px-[11px] py-[9px] font-mono text-[11.5px] leading-[1.5]" style={{ color: "var(--err)" }}>{env.message}</pre>}
        <div>
          <Button variant="primary" onClick={onRetry} disabled={busy} reason={busy ? "ставим прогон в очередь…" : undefined}>
            прогнать ещё раз
          </Button>
        </div>
        {error && (
          <p className="m-0 text-[12.5px]" style={{ color: "var(--err)" }}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

function Check({ asg, run, runs, user }: { asg: AssignmentView; run: Run; runs: Run[]; user: User }) {
  const navigate = useNavigate();
  const ws = useWorkspace(asg.id);
  const flow = useSubmitFlow(asg.id);
  const check = useServerCheck(asg.id);
  const tasks = asg.manifest.tasks as Task[];
  const total = run.total || tasks.length;
  const fails = failures(run, tasks);
  const [open, setOpen] = useState<Record<number, boolean>>(() => (fails[0] ? { [fails[0].n]: true } : {}));
  const [focus, setFocus] = useState<number | undefined>(fails[0]?.n);
  // Ключ показа: другой прогон или тот же, но завершившийся (результат пришёл
  // после открытия страницы) — раскрыта снова первая упавшая, как в макете.
  const shownKey = `${run.id}:${fails.length > 0}`;
  const [shownRun, setShownRun] = useState(shownKey);
  if (shownRun !== shownKey) {
    setShownRun(shownKey);
    setOpen(fails[0] ? { [fails[0].n]: true } : {});
    setFocus(fails[0]?.n);
  }

  const toTask = (n: number) => void navigate({ to: "/notebook/$assignmentId", params: { assignmentId: asg.id }, search: { task: n } });
  const toRun = (id: string) => void navigate({ to: "/notebook/$assignmentId/check", params: { assignmentId: asg.id }, search: { run: id } });
  const toggle = (n: number) => {
    const next = !open[n];
    setOpen((o) => ({ ...o, [n]: next }));
    if (next) setFocus(n);
  };
  const rerun = () => void check.run();

  const active = isActive(run);
  const envError = run.status === "error" || !!run.result?.env_error;
  const s = summary(run);
  const target = runs.find((r) => r.id === flow.elig?.run_id) ?? runs[0];

  return (
    <Shell wide user={user}>
      <Breadcrumb asg={asg} meta={runMeta(run)} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="border-bd px-6 pb-[26px] pt-[22px] lg:border-r">
          <Title total={total} />
          {active && <Pending run={run} />}
          {envError && <EnvError run={run} onRetry={rerun} busy={check.busy} error={check.error} />}
          <div className="mb-5 grid grid-cols-3 gap-3">
            <Stat id="passed" n={active ? "—" : s.passed} label="прошло" fg="var(--ok)" />
            <Stat id="failed" n={active ? "—" : s.failed} label="упало" fg="var(--err)" />
            <Stat id="idle" n={active ? "—" : s.idle} label="не запускалось" fg="var(--mut)" />
          </div>
          {active ? (
            <GridSkeleton total={total} />
          ) : (
            (!envError || (run.result?.tasks.length ?? 0) > 0) && (
              <>
                <TaskGrid run={run} total={total} focus={focus} onPick={toTask} />
                <Failures items={fails} tasks={tasks} nb={ws.data?.notebook as Notebook | undefined} open={open} onToggle={toggle} onOpenCell={toTask} />
              </>
            )
          )}
        </div>
        <aside className="flex flex-col gap-3.5 px-5 py-[22px]">
          <SubmitCard flow={flow} threshold={asg.manifest.threshold || total} total={total} runN={target?.n} />
          <RunsCard runs={runs} current={run.id} threshold={asg.manifest.threshold || total} onPick={toRun} />
          <AfterCard runN={target?.n} />
        </aside>
      </div>
    </Shell>
  );
}
